/**
 * Application Monitoring Utilities
 * Basic monitoring and logging for application health
 */
import { safeCurrentUrl } from "./safeCurrentUrl";

/**
 * Initialize monitoring system
 * Logs application initialization and key lifecycle events
 */
export function initializeMonitoring(): void {
  console.log('[Monitor] Application initialized', {
    timestamp: new Date(),
    message: 'Application initialized',
    context: {
      userAgent: navigator.userAgent,
      url: safeCurrentUrl(),
      referrer: document.referrer || 'direct',
    },
    level: 'info',
  });

  // Log page load event
  if (document.readyState === 'complete') {
    logPageLoad();
  } else {
    window.addEventListener('load', logPageLoad);
  }

  // Monitor performance if available
  if ('performance' in window && window.performance.timing) {
    setTimeout(() => {
      const timing = window.performance.timing;
      const loadTime = timing.loadEventEnd - timing.navigationStart;

      console.log('[Monitor] Page load performance', {
        loadTime: `${loadTime}ms`,
        domContentLoaded: `${timing.domContentLoadedEventEnd - timing.navigationStart}ms`,
        firstPaint: `${timing.responseEnd - timing.requestStart}ms`,
      });
    }, 0);
  }
}

/**
 * Log page load event
 */
function logPageLoad(): void {
  console.log('[Monitor] Page loaded', {
    timestamp: new Date(),
    message: 'Page loaded',
    context: {
      url: safeCurrentUrl(),
      title: document.title,
    },
    level: 'info',
  });
}

/**
 * Log a custom monitoring event
 */
export function logMonitoringEvent(
  event: string,
  data?: Record<string, any>
): void {
  console.log(`[Monitor] ${event}`, {
    timestamp: new Date(),
    event,
    data,
    level: 'info',
  });
}

/**
 * Track user action
 */
export function trackAction(
  action: string,
  category?: string,
  label?: string,
  value?: number
): void {
  console.log('[Monitor] User action', {
    action,
    category,
    label,
    value,
    timestamp: new Date(),
  });
}
